import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Phone, Users, Send, Sparkles, X,
  Plus, DollarSign, CheckCircle, PhoneCall, TrendingUp,
  BarChart3, Clock, Target, Timer, ArrowUp, ArrowDown,
  MessageCircle, ChevronRight, ChevronDown, Inbox
, Banknote, AlertTriangle } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, Legend
} from 'recharts';
import { STAGE_COLORS, COLORS } from '../../lib/constants';
import { formatCurrency, isWonStage, safeAmount, formatDate, toLocalDateString } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui';
// prequalificationService import removed: server-side RBAC now ensures prequalifications prop only contains current user's prequalifications

export default function EmployeeView({
  prequalifications,
  tenders = [],
  profiles = [],
  currentUser,
  onSelectLead,
  onLeadViewed,
  onStageChange,
  onAddNote,
  onShowNewLead,
  isLoading,
  newPrequalificationIds = [],
  unreadCount = 0,
  fetchAnalytics
}) {
  const { language, t, isRTL } = useLanguage();
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState('month');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [visibleTrendLines, setVisibleTrendLines] = useState(['prequalifications', 'enlisted', 'tender', 'booked']);

  React.useEffect(() => {
    if (fetchAnalytics) {
      fetchAnalytics({ dateRange }).then(data => {
        if (data) setAnalyticsData(data);
      });
    }
  }, [dateRange, fetchAnalytics]);

  // Update local state when prequalifications prop changes (from real-time socket updates)
  const [dashboardLeads, setDashboardLeads] = useState(prequalifications);
  React.useEffect(() => {
    const transformed = (prequalifications || []).map(prequalification => ({
      ...prequalification,
      clientName: prequalification.profile?.clientName || prequalification.profile?.contactPersonName || prequalification.contactPerson || prequalification.title || 'Unknown',
      stage: prequalification.status,
      amount: prequalification.finalAmount || prequalification.quotationAmount || prequalification.clientBudget || 0,
      inquiryDate: toLocalDateString(prequalification.createdAt),
      manager: prequalification.assignee ? prequalification.assignee.username : 'Unassigned',
      phone: prequalification.profile?.mobile || '',
      source: typeof prequalification.source === 'object' && prequalification.source !== null
        ? (prequalification.source.name || '')
        : (typeof prequalification.source === 'string' ? prequalification.source : '')
    }));
    setDashboardLeads(transformed);
  }, [prequalifications]);

  const today = toLocalDateString(new Date());

  // Since the server already filters prequalifications to only this user's prequalifications (RBAC),
  // all prequalifications in the prop are already "my prequalifications".
  const myLeads = useMemo(() => {
    return (dashboardLeads || [])
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [dashboardLeads]);

  const activeLeads = useMemo(() => {
    return myLeads.filter(l => l.stage !== 'Lost' && !isWonStage(l.stage) && l.approvalStatus !== 'PENDING_APPROVAL' && l.approvalStatus !== 'REJECTED');
  }, [myLeads]);

  // Combined follow-ups from Prequalifications, Tenders, and Profiles
  const allFollowUpLeads = useMemo(() => {
    const userLeads = (prequalifications || []).map(p => ({
      ...p,
      originalId: p.id,
      uiType: 'Prequalification',
      clientName: p.profile?.clientName || p.profile?.contactPersonName || p.contactPerson || p.title || 'Unknown',
      phone: p.profile?.mobile || p.phone || '',
      manager: p.assignee?.username || 'Unassigned',
      stage: p.status,
      nextCallDate: p.nextCallDate || null,
      nextCallTime: p.nextCallTime || null,
      createdAt: p.createdAt,
      approvalStatus: p.approvalStatus
    }));

    const mappedTenders = (tenders || []).map(t => ({
      ...t,
      originalId: t.id,
      id: `tender_${t.id}`,
      isTender: true,
      uiType: t.type === 'PROPOSAL' ? 'Proposal' : 'Tender',
      clientName: t.profile?.clientName || t.title || 'Tender',
      phone: t.profile?.mobile || '',
      manager: t.assignee?.username || 'Unassigned',
      stage: t.status,
      nextCallDate: t.nextCallDate || (t.followUpDate ? t.followUpDate.split('T')[0] : null),
      nextCallTime: t.nextCallTime || (t.followUpDate && t.followUpDate.includes('T') ? new Date(t.followUpDate).toTimeString().substring(0, 5) : null),
      createdAt: t.createdAt,
      approvalStatus: t.approvalStatus
    }));

    const mappedProfiles = (profiles || []).map(p => ({
      ...p,
      originalId: p.id,
      id: `profile_${p.id}`,
      isProfile: true,
      uiType: 'Profile',
      clientName: p.clientName || p.contactPersonName || 'No Name',
      phone: p.mobile || '',
      manager: p.assignedTo?.username || p.assignee?.username || 'Unassigned',
      stage: p.status || 'New',
      nextCallDate: p.nextCallDate || null,
      nextCallTime: p.nextCallTime || null,
      createdAt: p.createdAt
    }));

    return [...userLeads, ...mappedTenders, ...mappedProfiles];
  }, [prequalifications, tenders, profiles]);

  const followUpCategorized = useMemo(() => {
    const overdue = [];
    const todayCalls = [];
    const upcoming = [];
    const todayDateStr = toLocalDateString(new Date());
    const nowMs = Date.now();

    const activeItems = allFollowUpLeads.filter(l => {
      if (l.approvalStatus === 'PENDING_APPROVAL' || l.approvalStatus === 'REJECTED') {
        return false;
      }
      return (
        l.stage !== 'Enlisted' && l.stage !== 'Disqualified' &&
        (l.isTender || l.isProfile || l.inCallList === true || l.stage === 'Call List' || l.stage === 'Site Visited' || l.stage === 'New' || l.stage === 'Submitted' || l.stage === 'Pending' || !!l.nextCallDate || (l.siteVisitDate && l.siteVisitDate !== '-' && !l.siteVisited)) &&
        !isWonStage(l.stage) &&
        l.stage !== 'Lost' &&
        !(l.isProfile && (l.stage === 'Interested' || l.lastCallOutcome === 'connected_interested' || !!l.prequalificationId)) &&
        !(l.uiType === 'Prequalification' && !!l.tenderId)
      );
    });

    activeItems.forEach(item => {
      const targetDate = item.nextCallDate || (item.siteVisitDate && item.siteVisitDate !== '-' ? item.siteVisitDate : null);
      if (!targetDate) {
        if (nowMs - new Date(item.createdAt || 0).getTime() > 24 * 60 * 60 * 1000) {
          overdue.push({ ...item, priority: 'overdue' });
        } else {
          todayCalls.push({ ...item, priority: 'new' });
        }
        return;
      }

      const nextDateStr = targetDate.split('T')[0];
      if (nextDateStr < todayDateStr) {
        overdue.push({ ...item, priority: 'overdue' });
      } else if (nextDateStr === todayDateStr) {
        if (item.nextCallTime) {
          const scheduled = new Date(`${nextDateStr}T${item.nextCallTime}:00`);
          if (!isNaN(scheduled.getTime()) && nowMs - scheduled.getTime() > 24 * 60 * 60 * 1000) {
            overdue.push({ ...item, priority: 'overdue' });
          } else {
            todayCalls.push({ ...item, priority: 'today' });
          }
        } else {
          todayCalls.push({ ...item, priority: 'today' });
        }
      } else {
        upcoming.push({ ...item, priority: 'upcoming' });
      }
    });

    // Sort upcoming by target date ascending
    upcoming.sort((a, b) => {
      const dateA = a.nextCallDate || a.siteVisitDate || '';
      const dateB = b.nextCallDate || b.siteVisitDate || '';
      return dateA.localeCompare(dateB);
    });

    // Sort today calls by time
    todayCalls.sort((a, b) => {
      if (a.nextCallTime && b.nextCallTime) return a.nextCallTime.localeCompare(b.nextCallTime);
      return 0;
    });

    return { overdue, todayCalls, upcoming };
  }, [allFollowUpLeads]);

  const upcomingFollowUpsList = useMemo(() => {
    return [
      ...followUpCategorized.overdue,
      ...followUpCategorized.todayCalls,
      ...followUpCategorized.upcoming
    ].slice(0, 8);
  }, [followUpCategorized]);

  const stats = useMemo(() => {
    const callsToday = followUpCategorized.todayCalls.length;

    if (!analyticsData) {
      return {
        total: 0,
        wonInPeriod: 0,
        revenueInPeriod: 0,
        conversion: 0,
        callsToday,
        pipelineRevenue: 0,
        bookingsConfirmed: 0,
        rank: 'N/A',
        avgResponseTime: null,
        trendData: [],
        currentPeriodLeads: []
      };
    }

    // Calculate won count from leadsByStage - same source as the Prequalification Conversion Funnel
    // This ensures Quick Stats "Won" matches the funnel "Won" number exactly
    const wonFromStage = (() => {
      if (!analyticsData.leadsByStage) return analyticsData.bookedPeriodLeads;
      let total = 0;
      Object.entries(analyticsData.leadsByStage).forEach(([stageName, value]) => {
        if (isWonStage(stageName)) total += value;
      });
      return total;
    })();

    return {
      total: analyticsData.periodLeads,
      wonInPeriod: wonFromStage,
      revenueInPeriod: analyticsData.revenueInPeriod,
      conversion: analyticsData.conversionRate,
      callsToday,
      pipelineRevenue: analyticsData.pipelinePotential,
      bookingsConfirmed: wonFromStage,
      rank: Object.values(analyticsData.employeeStats || {})[0]?.rank || 'N/A',
      avgResponseTime: Object.values(analyticsData.employeeStats || {})[0]?.avgResponseTime || null,
      trendData: analyticsData.trendData || [],
      currentPeriodLeads: analyticsData.drilldownLeads || []
    };
  }, [analyticsData, followUpCategorized]);

  // Funnel data for my prequalifications
  const funnelStages = ['New', 'Contacted', 'Quoted', 'Site Visited', 'Won'];
  const funnelData = useMemo(() => {
    if (!analyticsData?.leadsByStage) return [];
    const counts = {};
    funnelStages.forEach(s => (counts[s] = 0));
    Object.entries(analyticsData.leadsByStage).forEach(([stageName, value]) => {
      let s = stageName === 'New Prequalification' ? 'New' : stageName;
      if (isWonStage(stageName)) s = 'Won';
      if (counts[s] !== undefined) counts[s] += value;
    });
    return funnelStages.map((s, i) => ({
      name: s.replace(' Scheduled', ''),
      originalStage: s,
      value: counts[s]
    }));
  }, [analyticsData]);



  return (
    <div className="space-y-4 md:space-y-6">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
            {t('salesDashboard')}
          </h1>
          <p className="text-gray-500 mt-1">
            {t('welcomeBack')},{' '}
            <span className="font-semibold text-gray-900">{currentUser?.name}</span>
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="flex bg-white rounded-xl shadow-sm border p-1">
            <button
              onClick={() => setDateRange('week')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${dateRange === 'week' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'
                }`}
            >
              {t('week')}
            </button>
            <button
              onClick={() => setDateRange('month')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${dateRange === 'month' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'
                }`}
            >
              {t('month')}
            </button>
            <button
              onClick={() => setDateRange('quarter')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${dateRange === 'quarter' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'
                }`}
            >
              {t('quarter')}
            </button>
            <button
              onClick={() => setDateRange('year')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${dateRange === 'year' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'
                }`}
            >
              {t('year')}
            </button>
            <button
              onClick={() => setDateRange('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${dateRange === 'all' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'
                }`}
            >
              All
            </button>
          </div>
          <button
            onClick={onShowNewLead}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-sm transition-all shadow-sm w-full sm:w-auto justify-center"
          >
            <Plus className="w-4 h-4" />
            <span>{t('addLead')}</span>
          </button>
        </div>
      </div>

      {/* Stats Grid - same style as OwnerDashboard */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-4">{t('quickStats')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">

          {/* Won This Month */}
          <div className="bg-white p-4 rounded-2xl border shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-blue-100 text-blue-600 rounded-xl">
                <CheckCircle className="w-5 h-5" />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate">{t('won')}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1">{stats.wonInPeriod}</h3>
            </div>
          </div>

          {/* My Prequalifications */}
          <div className="bg-white p-4 rounded-2xl border shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-amber-100 text-amber-600 rounded-xl">
                <Users className="w-5 h-5" />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate">{t('myLeads')}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1">{stats.total}</h3>
            </div>
          </div>

          {/* Conversion Rate */}
          <div className="bg-white p-4 rounded-2xl border shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-purple-100 text-purple-600 rounded-xl">
                <Target className="w-5 h-5" />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate">{t('conversionRate')}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1">{stats.conversion}%</h3>
            </div>
          </div>

          {/* Calls Today */}
          <div className="bg-white p-4 rounded-2xl border shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-pink-100 text-pink-600 rounded-xl">
                <PhoneCall className="w-5 h-5" />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate">{t('callsToday')}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1">{stats.callsToday}</h3>
            </div>
          </div>

          {/* Avg Response Time */}
          <div className="bg-white p-4 rounded-2xl border shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-cyan-100 text-cyan-600 rounded-xl">
                <Timer className="w-5 h-5" />
              </div>
              {stats.avgResponseTime !== null && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${stats.avgResponseTime <= 30 ? 'bg-green-100 text-green-700'
                    : stats.avgResponseTime <= 120 ? 'bg-amber-100 text-amber-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                  {stats.avgResponseTime <= 30 ? t('excellent')
                    : stats.avgResponseTime <= 120 ? t('good')
                      : t('needsImprovement')}
                </span>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate">{t('avgResponseTime')}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1">
                {stats.avgResponseTime !== null
                  ? stats.avgResponseTime < 60
                    ? `${stats.avgResponseTime}m`
                    : `${Math.round(stats.avgResponseTime / 60)}h`
                  : '-'}
              </h3>
            </div>
          </div>
        
          {/* Proposal Value (CRM) */}
          <div className="bg-white p-4 rounded-2xl border shadow-sm transition-all group relative overflow-hidden">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-indigo-100 text-indigo-600 rounded-xl group-hover:scale-110 transition-transform">
                <TrendingUp className="w-5 h-5" />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate" title="Proposal Value (CRM)">{t('pipelineValue') || 'Proposal Value (CRM)'}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1 truncate">{formatCurrency(analyticsData?.pipelinePotential || 0)}</h3>
            </div>
          </div>
        </div>
      </div>
      {/* Charts Row 1 */}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Revenue & Prequalifications Trend (Matching CEO Dashboard) */}
        <div className="lg:col-span-2 bg-white p-4 md:p-5 rounded-xl border shadow-sm">
          <div className="flex flex-row justify-between items-start mb-4 w-full gap-2">
            <h3 className="font-bold text-gray-800 flex items-start sm:items-center gap-2 text-sm md:text-base min-w-0">
              <BarChart3 className="w-4 h-4 shrink-0 mt-0.5 sm:mt-0" />
              <span className="leading-tight">{t('prequalTenderAwardTrend') || 'Prequalification, Tender & Award Won Trend'}</span>
            </h3>
            <div className="shrink-0">
              <Dropdown className="w-auto">
                <DropdownTrigger>
                  <button className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center gap-1 whitespace-nowrap">
                    {t('filterLines') || 'Filter Lines'}
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownTrigger>
                <DropdownPopover>
                  <DropdownMenu
                    className="w-56 p-1"
                    selectionMode="multiple"
                    selectedKeys={new Set(visibleTrendLines)}
                    onSelectionChange={(keys) => setVisibleTrendLines(Array.from(keys))}
                  >
                    {[
                      { id: 'prequalifications', label: t('prequalifications') || 'Prequalifications', color: 'text-blue-600' },
                      { id: 'enlisted', label: t('prequalEnlisted') || 'Prequalification Enlisted', color: 'text-purple-600' },
                      { id: 'tender', label: t('tendersProposals') || 'Tender/Proposal', color: 'text-amber-600' },
                      { id: 'booked', label: t('awardWon') || 'Award Won', color: 'text-emerald-600' }
                    ].map(line => (
                      <DropdownItem key={line.id} id={line.id}>
                        <span className={`font-medium ${line.color}`}>{line.label}</span>
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </DropdownPopover>
              </Dropdown>
            </div>
          </div>
          <div className="h-48 md:h-64">
            {isLoading ? (
              <div className="h-full flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : stats.trendData && stats.trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} width={30} allowDecimals={false} domain={[0, 'auto']} />
                  <Tooltip formatter={(value, name) => [value.toLocaleString(), name]} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Area
                    type="monotone"
                    dataKey="prequalifications"
                    stroke="#3B82F6"
                    fill="#3B82F6"
                    fillOpacity={0.1}
                    name={t('prequalifications') || 'Prequalifications'}
                    hide={!visibleTrendLines.includes('prequalifications')}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="enlisted"
                    stroke="#8B5CF6"
                    fill="#8B5CF6"
                    fillOpacity={0.1}
                    name={t('prequalEnlisted') || 'Prequalification Enlisted'}
                    hide={!visibleTrendLines.includes('enlisted')}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="tender"
                    stroke="#F59E0B"
                    fill="#F59E0B"
                    fillOpacity={0.1}
                    name={t('tendersProposals') || 'Tender/Proposal'}
                    hide={!visibleTrendLines.includes('tender')}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="booked"
                    stroke="#10B981"
                    fill="#10B981"
                    fillOpacity={0.15}
                    name={t('awardWon') || 'Award Won'}
                    hide={!visibleTrendLines.includes('booked')}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-gray-400">
                <BarChart3 className="w-8 h-8 stroke-[1.5] mb-2 text-gray-300" />
                <p className="text-xs font-medium">{t('noDataAvailable') || 'No trend data available'}</p>
              </div>
            )}
          </div>
        </div>

        {/* My Prequalification Funnel */}
        <div className="bg-white p-4 md:p-5 rounded-xl border shadow-sm">
          <h3 className="font-bold text-gray-800 mb-4 text-sm md:text-base">
            {t('leadConversionFunnel')}
          </h3>
          <div className="space-y-2">
            {funnelData.map((item, i) => {
              const maxVal = Math.max(...funnelData.map(d => d.value), 1);
              const width = (item.value / maxVal) * 100;
              const colors = STAGE_COLORS[item.originalStage] || STAGE_COLORS['New'] || { bg: 'bg-blue-100', text: 'text-blue-700' };

              return (
                <div key={item.name} className="flex items-center gap-2">
                  <div className="w-16 md:w-24 text-[10px] md:text-xs text-gray-600 truncate">{item.name}</div>
                  <div className="flex-1 bg-gray-100 rounded-full h-5 md:h-6 overflow-hidden">
                    <div
                      className={`h-full ${colors.bg} flex items-center justify-end pr-2`}
                      style={{ width: `${Math.max(width, 10)}%` }}
                    >
                      <span className={`text-[10px] md:text-xs font-bold ${colors.text}`}>{item.value}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Charts Row 2: Active Prequalifications, Upcoming Calls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* My Active Prequalifications */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden flex flex-col h-[400px]">
          <div className="p-4 border-b bg-gray-50/50 flex items-center justify-between flex-shrink-0">
            <h3 className="font-bold text-gray-800 flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-blue-600" />
              {t('myActiveLeads')}
            </h3>
            <button
              onClick={() => navigate('/prequalifications')}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            >
              {t('viewAll')} <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="divide-y overflow-y-auto flex-1 custom-scrollbar">
            {activeLeads.slice(0, 5).map(prequalification => {
              const createdAt = new Date(prequalification.createdAt || 0);
              const diffDays = Math.ceil(Math.abs(new Date() - createdAt) / (1000 * 60 * 60 * 24));
              const timeAgo = diffDays <= 1 ? '1 day ago' : `${diffDays} days ago`;
              const stageColors = STAGE_COLORS[prequalification.stage] || {};

              return (
                <div
                  key={prequalification.id}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => onSelectLead && onSelectLead(prequalification)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-[11px] flex-shrink-0">
                      {prequalification.clientName?.substring(0, 2).toUpperCase() || 'U'}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 text-[13px] truncate">{prequalification.clientName}</p>
                      <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5 truncate">
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3 text-gray-400" /> {prequalification.phone}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${stageColors.bg || 'bg-gray-100'} ${stageColors.text || 'text-gray-600'}`}>
                      {prequalification.stage}
                    </span>
                    <span className="text-[10px] text-gray-400">{timeAgo}</span>
                  </div>
                </div>
              );
            })}
            {activeLeads.length === 0 && (
              <div className="p-8 text-center text-gray-400 text-xs">
                {t('noLeadsFound')}
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Calls / Follow ups */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden flex flex-col h-[400px]">
          <div className="p-4 border-b bg-gray-50/50 flex items-center justify-between flex-shrink-0">
            <h3 className="font-bold text-gray-800 flex items-center gap-2 text-sm">
              <PhoneCall className="w-4 h-4 text-green-600" />
              {t('upcomingCalls') || 'Upcoming Follow ups'}
            </h3>
            <button
              onClick={() => navigate('/follow-up')}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            >
              {t('viewAll') || 'View All'} <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="divide-y overflow-y-auto flex-1 custom-scrollbar">
            {upcomingFollowUpsList.length > 0 ? upcomingFollowUpsList.map(call => {
              const stageColors = STAGE_COLORS[call.stage] || {};
              const isOverdue = call.priority === 'overdue';
              const isToday = call.priority === 'today' || call.priority === 'new';

              return (
                <div
                  key={call.id}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => {
                    if (call.uiType === 'Tender' || call.uiType === 'Proposal') {
                      navigate('/tenders');
                    } else if (call.uiType === 'Profile') {
                      navigate('/profiles');
                    } else {
                      onSelectLead && onSelectLead(call);
                    }
                  }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                      isOverdue ? 'bg-red-100 text-red-600' : isToday ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                    }`}>
                      <PhoneCall className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900 text-[13px] truncate">{call.clientName}</p>
                        {call.uiType && call.uiType !== 'Prequalification' && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-indigo-50 text-indigo-600 border border-indigo-100">
                            {call.uiType}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${stageColors.bg || 'bg-gray-100'} ${stageColors.text || 'text-gray-600'}`}>
                          {call.stage}
                        </span>
                        {call.phone && (
                          <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                            <Phone className="w-2.5 h-2.5" /> {call.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-[11px] font-bold flex items-center justify-end gap-1 ${
                      isOverdue ? 'text-red-600' : isToday ? 'text-green-600' : 'text-gray-700'
                    }`}>
                      <Clock className="w-3 h-3" /> {call.nextCallTime || 'TBD'}
                    </p>
                    <p className={`text-[10px] mt-0.5 ${isOverdue ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                      {isOverdue 
                        ? (call.nextCallDate ? `Overdue (${formatDate(call.nextCallDate)})` : 'Overdue (>24h)')
                        : isToday 
                          ? 'Today'
                          : (call.nextCallDate ? formatDate(call.nextCallDate) : 'Upcoming')}
                    </p>
                  </div>
                </div>
              );
            }) : (
              <div className="p-8 text-center text-gray-400 text-xs">
                {t('noUpcomingCalls') || 'No upcoming follow ups scheduled.'}
              </div>
            )}
          </div>
        </div>
      </div>



    </div>
  );
}
